import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faFileExcel,
  faDownload,
  faArrowRight,
  faArrowLeft,
  faSpinner,
  faSyncAlt,
  faInfoCircle,
  faCheckCircle,
  faUsers,
  faExclamationTriangle,
  faCopy,
  faCloudUploadAlt,
  faFileAlt,
  faSearch,
  faPaperPlane,
  faClock,
  faBullhorn,
  faShieldAlt,
  faLayerGroup
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from 'xlsx';
import { getToken } from '../../function/token.jsx';
import domain from "../../../reusecomponent/domain.jsx";
import { ErrorToastContainer, fetchTemplateDetails, SuccessToastContainer, WorningToastContainer } from '../../function/CustomFunction';
import ComingSoon from './ComingSoon';
import Contactlist from "./Contactlist.jsx";
import Templatelist from "./templatelist.jsx";
import TemplatePreview from "./TemplatePreview.jsx";
import RetargetingCampaign from "./RetargetingCampaign.jsx";
import UploadModal from "../../../reusecomponent/UploadModal.jsx";
import './managecampaigns.css';

function BroadcastType() {
  const { type } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [campaignName, setCampaignName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateVariables, setTemplateVariables] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isSendingCampaign, setIsSendingCampaign] = useState(false);

  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [file, setFile] = useState(null);
  const [isPolicyConfirmed, setIsPolicyConfirmed] = useState(false);

  // Schedule date and time state variables
  const [scheduleDate, setScheduleDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });

  const [scheduleTime, setScheduleTime] = useState(() => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`; // Format: HH:MM:SS
  });

  const initialVariableValue = useMemo(() => ({
    value_type: "",
    value: null,
    content_type: null
  }), []);

  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [fileAnalysis, setFileAnalysis] = useState(null);
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Real contacts data from API
  const [customers, setCustomers] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const fileUrl = useRef("");
  const fileType = useRef(null);
  const csvFileName = useRef(null);
  const [csvUplodeProgressModel, setcsvUplodeProgressModel] = useState(false);

  // Update the steps based on campaign type
  const [steps, setSteps] = useState([
    { id: 1, name: "Campaign Name" },
    { id: 2, name: "Target Audience" },
    { id: 3, name: "Select Audience" },
    { id: 4, name: "Create Message" },
    { id: 5, name: "Test Campaign" },
    { id: 6, name: "Preview & Send" },
  ]);

  // Update steps when campaign type changes
  useEffect(() => {
    if (type === "csv") {
      setSteps([
        { id: 1, name: "Campaign Name" },
        { id: 2, name: "Upload CSV" },
        { id: 3, name: "Select Audience" },
        { id: 4, name: "Create Message" },
        { id: 5, name: "Test Campaign" },
        { id: 6, name: "Preview & Send" },
      ]);
    } else {
      setSteps([
        { id: 1, name: "Campaign Name" },
        { id: 2, name: "Select Audience" },
        { id: 3, name: "Create Message" },
        { id: 4, name: "Test Campaign" },
        { id: 5, name: "Preview & Send" },
      ]);
    }
  }, [type]);

  // Fetch templates when reaching Create Message step
  useEffect(() => {
    if ((type === "csv" && currentStep === 4) || (type === "broadcast" && currentStep === 3)) {
      if (!templates || templates.length === 0) {
        fetchTemplates();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, type]);

  // Fetch contacts when reaching Select Audience step
  useEffect(() => {
    if ((type === "csv" && currentStep === 3) || (type === "broadcast" && currentStep === 2)) {
      if (!customers || customers.length === 0) {
        fetchContacts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, type]);

  // Function to fetch contacts from API
  const fetchContacts = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    setLoadingContacts(true);
    try {
      const response = await fetch(`${domain()}/contact`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        const transformedContacts = result.data.map((contact, index) => ({
          id: contact.id || contact._id || index + 1,
          name: contact.name || 'Unknown',
          mobile: contact.number || contact.mobile || '',
          email: contact.email || '',
          tag: contact.city || contact.state || 'Contact',
          selected: false,
          address: contact.address || '',
          city: contact.city || '',
          district: contact.district || '',
          state: contact.state || '',
          pin: contact.pin || ''
        }));

        setCustomers(transformedContacts);
      } else if (result.data) {
        try {
          const decoded = JSON.parse(atob(result.data));
          const transformedContacts = decoded.map((contact, index) => ({
            id: contact.id || contact._id || index + 1,
            name: contact.name || 'Unknown',
            mobile: contact.number || contact.mobile || '',
            email: contact.email || '',
            tag: contact.city || contact.state || 'Contact',
            selected: false,
            address: contact.address || '',
            city: contact.city || '',
            district: contact.district || '',
            state: contact.state || '',
            pin: contact.pin || ''
          }));
          setCustomers(transformedContacts);
        } catch {
          setCustomers([]);
          ErrorToastContainer('Failed to decode contacts data');
        }
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setCustomers([]);
      ErrorToastContainer('Failed to fetch contacts');
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  const FilePathUpdate = (path) => {
    setIsUploadingImage(false);
    if (path) {
      let url = `${domain()}/whatsapp/campaign/files/${path}`;
      if (!url) {
        WorningToastContainer("Please Select Valid File Url");
        return;
      }
      fileUrl.current = url;
      handleVariableMapping('header', 'STATIC', url, fileType.current === null ? "TEXT" : fileType.current);
    }
    setFile(null);
  };

  const CsvPathUpdate = (path) => {
    if (path) {
      csvFileName.current = path;
    }
    setcsvUplodeProgressModel(false);
  };

  // Fetch templates from API
  const fetchTemplates = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    setLoadingTemplates(true);
    try {
      const response = await fetch(`${domain()}/manage-template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.status) {
        const approvedTemplates = (data?.data || []).filter(
          item => item.status === "APPROVED" && (item.category === 'MARKETING' || item.category === 'UTILITY')
        );
        setTemplates(approvedTemplates);
      } else {
        WorningToastContainer(data.message);
      }
    } catch (error) {
      ErrorToastContainer('Error fetching templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Handle template selection
  const handleTemplateSelection = async (template) => {
    if (selectedTemplate != null && selectedTemplate.id === template.id) {
      return;
    }

    const detailedTemplate = await fetchTemplateDetails(template.id);
    if (detailedTemplate && detailedTemplate.status) {
      const detailedData = detailedTemplate.data;
      setSelectedTemplate(detailedData);
      let newVars = {};

      if (detailedData.components) {
        try {
          const components = Array.isArray(detailedData.components)
            ? detailedData.components
            : JSON.parse(detailedData.components);

          components.forEach((component) => {
            if (component.type === "HEADER") {
              fileType.current = null;
              if (component.format === 'TEXT') {
                if (component.text) {
                  const matches = component.text.match(/\{\{(\d+)\}\}/g) || [];
                  matches.forEach(() => {
                    newVars['header'] = { ...initialVariableValue };
                  });
                }
              } else if (component.format === 'IMAGE') {
                fileType.current = 'IMAGE';
                newVars['header'] = { ...initialVariableValue, content_type: 'IMAGE' };
              } else if (component.format === 'VIDEO') {
                fileType.current = 'VIDEO';
                newVars['header'] = { ...initialVariableValue, content_type: 'VIDEO' };
              } else if (component.format === 'AUDIO') {
                fileType.current = 'AUDIO';
                newVars['header'] = { ...initialVariableValue, content_type: 'AUDIO' };
              } else if (component.format === 'LOCATION') {
                WorningToastContainer("Template in Location not allowed");
                return;
              } else if (component.format === 'DOCUMENT') {
                fileType.current = 'DOCUMENT';
                newVars['header'] = { ...initialVariableValue, content_type: 'DOCUMENT' };
              }
            } else if (component.type === "BODY" && component.text) {
              const matches = component.text.match(/\{\{(\d+)\}\}/g) || [];
              matches.forEach((m) => {
                const idx = m.replace(/[^\d]/g, "");
                newVars[`body_${idx}`] = { ...initialVariableValue };
              });
            }
          });
        } catch (error) {
          console.error('Error parsing components JSON:', error);
        }
      }

      setTemplateVariables(newVars);
    } else {
      setSelectedTemplate(template);
      let newVars = {};
      if (template.variables && template.variables.length > 0) {
        template.variables.forEach(v => {
          newVars[v] = { ...initialVariableValue };
        });
      }
      setTemplateVariables(newVars);
    }
    fileUrl.current = "";
  };

  // Variable Mapping handler
  const handleVariableMapping = (variable, valueType, mappedValue, content_type) => {
    let newobj;
    const isHeaderVar = variable.startsWith('header');
    const resolvedContentType = isHeaderVar
      ? (content_type !== undefined ? content_type : (fileType.current === null ? 'TEXT' : fileType.current))
      : 'TEXT';

    if (mappedValue === undefined) {
      newobj = {
        value_type: valueType,
        value: null,
        content_type: null,
      };
    } else {
      if (valueType === 'STATIC') {
        newobj = {
          value_type: valueType,
          value: mappedValue,
          content_type: resolvedContentType,
        };
      } else {
        newobj = {
          value_type: valueType,
          value: mappedValue,
          content_type: resolvedContentType,
        };

        if (variable === 'header' && (fileType.current === 'IMAGE' || fileType.current === 'VIDEO' || fileType.current === 'DOCUMENT')) {
          try {
            if (type === 'broadcast') {
              if (customers[0] && customers[0][mappedValue]) {
                new URL(customers[0][mappedValue]);
                fileUrl.current = customers[0][mappedValue];
              }
            } else {
              if (csvPreview[0] && csvPreview[0][mappedValue]) {
                new URL(csvPreview[0][mappedValue]);
                fileUrl.current = csvPreview[0][mappedValue];
              }
            }
          } catch {
            WorningToastContainer("Please Select Valid File Url");
            return false;
          }
        }
      }
    }

    setTemplateVariables(prev => ({
      ...prev,
      [variable]: newobj
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!campaignName || campaignName.trim() === '') {
        ErrorToastContainer('Please enter campaign name');
        return;
      }
    } else if ((currentStep === 3 && type === 'broadcast') || (currentStep === 4 && type === 'csv')) {
      for (const key in templateVariables) {
        const { value_type, value, content_type } = templateVariables[key];
        if (!["STATIC", "DYNAMIC"].includes(value_type)) {
          WorningToastContainer(`Please select value type for ${key}`);
          return false;
        }

        if (key === 'header' && (content_type === 'IMAGE' || content_type === 'VIDEO' || content_type === 'DOCUMENT') && value_type === 'STATIC') {
          try {
            new URL(value);
          } catch (e) {
            WorningToastContainer(`Please Select Valid ${content_type} Url`);
            return false;
          }
        }

        if ((value === null || value === '') && value_type === 'STATIC') {
          WorningToastContainer(`Please enter value for ${key}`);
          return false;
        }

        if ((value === null || value === '') && value_type === 'DYNAMIC') {
          WorningToastContainer(`Please select contact field for ${key}`);
          return false;
        }
      }
    }

    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setCustomers(prev => prev.map(c => ({ ...c, selected: newSelectAll })));
  };

  const handleSelectCustomer = useCallback((id) => {
    setCustomers(prev => {
      const updated = prev.map(c => c.id === id ? { ...c, selected: !c.selected } : c);
      setSelectAll(updated.every(c => c.selected));
      return updated;
    });
  }, []);

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        ErrorToastContainer("Only Excel spreadsheets (.xlsx, .xls) are supported.");
        return;
      }
      setcsvUplodeProgressModel(true);
      setCsvFile(file);
      setUploadedFile(file);
      processFile(file, allowDuplicates);
    }
  };

  const processFile = (fileToProcess, allowDup = allowDuplicates) => {
    const file = fileToProcess || csvFile;
    if (!file) {
      WorningToastContainer("Please select an Excel file first");
      return;
    }
    setIsProcessingFile(true);
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      parseExcelFile(file, allowDup);
    } else {
      ErrorToastContainer("Only Excel spreadsheets (.xlsx, .xls) are allowed");
      setIsProcessingFile(false);
    }
  };

  const parseCSVFile = (file, allowDup = allowDuplicates) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n');

        if (lines.length < 2) {
          ErrorToastContainer('Invalid file: File must contain headers and at least one data row');
          setCsvPreview(null);
          setFileAnalysis(null);
          setIsProcessingFile(false);
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const requiredHeaders = ['Name', 'Number'];

        const missingHeaders = requiredHeaders.filter(header =>
          !headers.some(h => h.toLowerCase() === header.toLowerCase())
        );

        if (missingHeaders.length > 0) {
          ErrorToastContainer(`Invalid file: Missing required headers: ${missingHeaders.join(', ')}`);
          setCsvPreview(null);
          setFileAnalysis(null);
          setIsProcessingFile(false);
          return;
        }

        const data = [];
        let validContacts = 0;
        let invalidFormat = 0;
        let duplicates = 0;
        const seenNumbers = new Set();

        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim() === '') continue;

          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          const row = {};

          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });

          if (row.Name && row.Number) {
            const cleanNumber = String(row.Number).trim();
            if (seenNumbers.has(cleanNumber)) {
              duplicates++;
              if (!allowDup) {
                continue;
              }
            }
            seenNumbers.add(cleanNumber);

            data.push({
              id: validContacts + 1,
              name: row.Name,
              mobile: row.Number,
              parameter1: row.parameter1 || '',
              parameter2: row.parameter2 || '',
              parameter3: row.parameter3 || '',
              parameter4: row.parameter4 || '',
              parameter5: row.parameter5 || '',
              selected: true,
            });
            validContacts++;
          } else {
            invalidFormat++;
          }
        }

        setCsvPreview(data);
        setFileAnalysis({
          totalContacts: allowDup ? (data.length + invalidFormat) : (data.length + invalidFormat + duplicates),
          validContacts: validContacts,
          invalidFormat: invalidFormat,
          duplicates: duplicates,
          allowDuplicates: allowDup
        });
        SuccessToastContainer('File processed successfully!');
      } catch (err) {
        ErrorToastContainer('Error parsing CSV file');
      } finally {
        setIsProcessingFile(false);
      }
    };
    reader.readAsText(file);
  };

  const parseExcelFile = (file, allowDup = allowDuplicates) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const excelData = new Uint8Array(e.target.result);
      try {
        const workbook = XLSX.read(excelData, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          ErrorToastContainer('Invalid file: File must contain headers and at least one data row');
          setCsvPreview(null);
          setFileAnalysis(null);
          setIsProcessingFile(false);
          return;
        }

        const headers = jsonData[0].map(h => h ? h.toString().trim() : '');
        const requiredHeaders = ['Name', 'Number'];

        const missingHeaders = requiredHeaders.filter(header =>
          !headers.some(h => h.toLowerCase() === header.toLowerCase())
        );

        if (missingHeaders.length > 0) {
          ErrorToastContainer(`Invalid file: Missing required headers: ${missingHeaders.join(', ')}`);
          setCsvPreview(null);
          setFileAnalysis(null);
          setIsProcessingFile(false);
          return;
        }

        const parsedContacts = [];
        let validContacts = 0;
        let invalidFormat = 0;
        let duplicates = 0;
        const seenNumbers = new Set();

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0) continue;

          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = row[index] ? row[index].toString().trim() : '';
          });

          if (rowData.Name && rowData.Number) {
            const cleanNumber = String(rowData.Number).trim();
            if (seenNumbers.has(cleanNumber)) {
              duplicates++;
              if (!allowDup) {
                continue;
              }
            }
            seenNumbers.add(cleanNumber);

            parsedContacts.push({
              id: validContacts + 1,
              name: rowData.Name,
              mobile: rowData.Number,
              parameter1: rowData.parameter1 || '',
              parameter2: rowData.parameter2 || '',
              parameter3: rowData.parameter3 || '',
              parameter4: rowData.parameter4 || '',
              parameter5: rowData.parameter5 || '',
              selected: true,
            });
            validContacts++;
          } else {
            invalidFormat++;
          }
        }

        setCsvPreview(parsedContacts);
        setFileAnalysis({
          totalContacts: allowDup ? (parsedContacts.length + invalidFormat) : (parsedContacts.length + invalidFormat + duplicates),
          validContacts: validContacts,
          invalidFormat: invalidFormat,
          duplicates: duplicates,
          allowDuplicates: allowDup
        });
        SuccessToastContainer('File processed successfully!');
      } catch (error) {
        ErrorToastContainer('Error parsing Excel file. Please make sure the file format is correct.');
        console.error('Excel parsing error:', error);
        setCsvPreview(null);
        setFileAnalysis(null);
      } finally {
        setIsProcessingFile(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const validatePhoneNumber = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10;
  };

  // Send Campaign Handler
  const sendCampaign = async (mode) => {
    if (mode === 'Production' && !isPolicyConfirmed) {
      ErrorToastContainer('Please confirm message compliance before sending.');
      return;
    }

    if (mode === 'Test' && (!testPhoneNumber || !validatePhoneNumber(testPhoneNumber))) {
      WorningToastContainer("Please enter a valid phone number!");
      return;
    }

    if (mode === 'Test') {
      setIsSendingTest(true);
    } else {
      setIsSendingCampaign(true);
    }

    const selectedContacts = type === "csv"
      ? csvPreview || []
      : customers.filter(c => c.selected);

    if (selectedContacts.length === 0) {
      ErrorToastContainer('Please select at least one contact.');
      setIsSendingCampaign(false);
      setIsSendingTest(false);
      return;
    }

    try {
      const token = getToken();
      let response;

      if (type === "broadcast") {
        const endpoint = mode === 'Test' ? `${domain()}/campaign_test` : `${domain()}/campaign_by_contact`;
        const contactList = selectedContacts.map(c => ({
          name: c.name || 'Unknown',
          phone_number: c.mobile || '',
          contact_id: c.id || 0
        }));

        let requestBody = {};
        if (mode === 'Test') {
          requestBody = {
            templateVariables: templateVariables,
            template_id: selectedTemplate.id,
            phonenumber: testPhoneNumber,
            contact_id: contactList[0]?.contact_id || 0,
            campaign_type: "CONTACT"
          };
        } else {
          requestBody = {
            campaign_name: campaignName,
            total_number: contactList.length,
            templateVariables: templateVariables,
            template_id: selectedTemplate.id,
            campaign_date: scheduleDate,
            campaign_time: scheduleTime,
            contact_list: contactList,
            campaign_type: "CONTACT"
          };
        }

        response = await fetch(endpoint, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });
      } else {
        const endpoint = mode === 'Test' ? `${domain()}/campaign_test` : `${domain()}/campaign_by_excel`;
        const formData = new FormData();

        if (mode === 'Production' && uploadedFile) {
          formData.append('file_id', csvFileName.current || '');
        }

        formData.append('campaign_name', campaignName || "");
        formData.append('total_number', selectedContacts.length.toString());
        formData.append('campaign_type', "CSV");
        formData.append('allow_duplicates', allowDuplicates ? '1' : '0');
        formData.append('template_id', selectedTemplate.id.toString());
        formData.append('templateVariables', JSON.stringify(templateVariables));

        if (mode === 'Production') {
          formData.append('campaign_date', scheduleDate);
          formData.append('campaign_time', scheduleTime);
        } else {
          const testContact = { ...csvPreview[0] };
          delete testContact.id;
          delete testContact.selected;
          formData.append('csv_first_contact_list', JSON.stringify(testContact));
          formData.append('phonenumber', testPhoneNumber);
        }

        response = await fetch(endpoint, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
      }

      const result = await response.json();
      if (result.status && mode === 'Production') {
        SuccessToastContainer('Campaign scheduled successfully!');
        navigate('/client-area/whatsapp/manage-campaigns');
      } else if (result.status && mode === 'Test') {
        SuccessToastContainer(result.message || 'Test message sent!');
      } else {
        ErrorToastContainer(`${result.message || 'Error sending campaign'}`);
      }
    } catch (error) {
      console.error('Campaign sending error:', error);
      ErrorToastContainer('Internal Server Error. Please try again.');
    } finally {
      setIsSendingCampaign(false);
      setIsSendingTest(false);
    }
  };

  // Sample data for Template Preview
  const samplePreviewData = useMemo(() => {
    if (type === "csv" && csvPreview && csvPreview.length > 0) {
      return csvPreview[0];
    }
    if (type === "broadcast" && customers && customers.length > 0) {
      return customers.find(c => c.selected) || customers[0];
    }
    return null;
  }, [type, csvPreview, customers]);

  if (type === "retargeting") {
    return <RetargetingCampaign />;
  }

  if (type !== "csv" && type !== "broadcast") {
    return <ComingSoon campaignType={type} />;
  }

  return (
    <div className="main-panel w-100" style={{ minHeight: '85vh' }}>
      <div className="content-wrapper p-2 p-md-3 pt-1">
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-3">

            {/* Progress Step Bar */}
            <div className="mb-3 campaign-stepper-container">
              <div className="d-flex justify-content-between position-relative campaign-stepper-track">
                <div
                  className="position-absolute"
                  style={{
                    height: "3px",
                    backgroundColor: "#e2e8f0",
                    width: "100%",
                    top: "14px",
                    zIndex: 0,
                  }}
                ></div>

                <div
                  className="position-absolute"
                  style={{
                    height: "3px",
                    backgroundColor: "#075E54",
                    width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                    top: "14px",
                    zIndex: 0,
                    transition: "width 0.3s ease-in-out",
                  }}
                ></div>

                {steps.map((step) => (
                  <div key={step.id} className="text-center position-relative campaign-step-item" style={{ flex: 1, zIndex: 1 }}>
                    <div
                      className={`rounded-circle d-flex align-items-center justify-content-center mb-1 mx-auto fw-bold campaign-step-circle ${currentStep > step.id
                          ? "bg-success text-white"
                          : currentStep === step.id
                            ? "text-white"
                            : "bg-light text-muted border"
                        }`}
                      style={{
                        width: "30px",
                        height: "30px",
                        backgroundColor: currentStep === step.id ? '#075E54' : undefined,
                        fontSize: '12px'
                      }}
                    >
                      {currentStep > step.id ? <FontAwesomeIcon icon={faCheck} /> : step.id}
                    </div>
                    <div className={`campaign-step-title small text-truncate ${currentStep === step.id ? "text-dark fw-bold active-step-title" : "text-muted"}`}>
                      {step.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 1: Campaign Name & Schedule */}
            {currentStep === 1 && (
              <div className="campaign-step-card w-100">
                <div className="campaign-step-scroll-body">
                  <div className="mb-3">
                    <span className="badge px-2.5 py-1 rounded-pill mb-1" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.72rem)', letterSpacing: '0.5px' }}>
                      STEP 1 • BASIC INFO
                    </span>
                    <h4 className="mb-1 fw-bold text-dark fs-5">Campaign Name &amp; Scheduling</h4>
                    <p className="text-muted mb-0 small">Set a clear title and timing for your WhatsApp broadcast.</p>
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label fw-semibold small mb-1">Campaign Name *</label>
                    <input
                      type="text"
                      className="form-control rounded-3"
                      placeholder="e.g. Festival_Offer_Announcement"
                      value={campaignName}
                      maxLength={50}
                      onChange={(e) => setCampaignName(e.target.value)}
                    />
                  </div>

                  <div className="row g-3 mb-2">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small mb-1">Schedule Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small mb-1">Schedule Time *</label>
                      <input
                        type="time"
                        className="form-control"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        step="1"
                      />
                    </div>
                  </div>
                </div>

                <div className="campaign-step-footer d-flex justify-content-end align-items-center">
                  <button 
                    type="button"
                    className="btn btn-campaign-primary px-4 px-md-5 py-1.5 rounded-pill fw-semibold shadow"
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handleNextStep}
                  >
                    Next <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 (CSV): Upload Audience */}
            {currentStep === 2 && type === "csv" && (
              <div className="campaign-step-card w-100">
                {/* Scrollable Body */}
                <div className="campaign-step-scroll-body">
                  {/* Header Title */}
                  <div className="text-center mb-3">
                    <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.75rem)', letterSpacing: '0.5px' }}>
                      STEP 2 • AUDIENCE IMPORT
                    </span>
                    <h3 className="fw-bold text-dark mb-1" style={{ fontSize: 'var(--font-size-2xl, 1.5rem)', letterSpacing: '-0.5px' }}>Upload Your Audience File</h3>
                    <p className="text-muted mx-auto mb-0" style={{ maxWidth: '640px', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                      Upload your contact spreadsheet (.xlsx or .xls). Your file must include <strong>Name</strong> and <strong>Number</strong> columns.
                    </p>
                  </div>

                  {/* Upload Modal if active */}
                  {csvUplodeProgressModel && (
                    <UploadModal FilePathUpdate={CsvPathUpdate} file={csvFile} url={`${domain()}/whatsapp/campaign/uplode-file`} />
                  )}

                  {/* Modern Drag & Drop Upload Zone */}
                  <div 
                    className="card border-0 shadow-sm mb-3"
                    style={{
                      borderRadius: '16px',
                      background: isDragging ? '#f0fdf4' : '#ffffff',
                      border: isDragging ? '2px dashed #16a34a' : '2px dashed #cbd5e1',
                      transition: 'all 0.25s ease',
                      cursor: 'pointer'
                    }}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const droppedFile = e.dataTransfer.files[0];
                      if (droppedFile) {
                        if (!droppedFile.name.endsWith('.xlsx') && !droppedFile.name.endsWith('.xls')) {
                          ErrorToastContainer("Only Excel spreadsheets (.xlsx, .xls) are supported.");
                          return;
                        }
                        setcsvUplodeProgressModel(true);
                        setCsvFile(droppedFile);
                        setUploadedFile(droppedFile);
                        processFile(droppedFile, allowDuplicates);
                      }
                    }}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <div className="card-body text-center py-4 px-3">
                      {/* Hidden Native File Input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="d-none"
                        id="csvFileInput"
                        accept=".xlsx,.xls"
                        onChange={handleCsvUpload}
                      />

                      {/* Animated Glow Icon Container */}
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(7,94,84,0.1) 0%, rgba(18,140,126,0.18) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                        boxShadow: '0 6px 16px rgba(7,94,84,0.1)'
                      }}>
                        <FontAwesomeIcon icon={faCloudUploadAlt} style={{ fontSize: '28px', color: 'var(--companycolor, #075E54)' }} />
                      </div>

                      <h5 className="fw-bold text-dark mb-1" style={{ fontSize: 'var(--font-size-base, 1rem)' }}>
                        {csvFile ? "Spreadsheet Attached" : "Drag and drop your spreadsheet here"}
                      </h5>
                      <p className="text-muted small mb-2" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                        {csvFile 
                          ? "Click below to change or re-upload a different file"
                          : "or browse from your local files to get started"}
                      </p>

                      {/* Format Badges */}
                      <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
                        <span className="badge rounded-pill bg-light text-secondary border px-3 py-1" style={{ fontSize: 'var(--font-size-xs, 0.75rem)', fontWeight: 600 }}>.XLSX</span>
                        <span className="badge rounded-pill bg-light text-secondary border px-3 py-1" style={{ fontSize: 'var(--font-size-xs, 0.75rem)', fontWeight: 600 }}>.XLS</span>
                      </div>

                      {/* Primary Button & Download Sample */}
                      <div className="d-flex flex-wrap justify-content-center align-items-center gap-3">
                        <button 
                          type="button" 
                          className="btn btn-campaign-primary px-4 py-2 rounded-pill fw-semibold shadow-sm"
                          style={{
                            fontSize: 'var(--font-size-sm, 0.875rem)'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current && fileInputRef.current.click();
                          }}
                        >
                          <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                          {csvFile ? "Replace Spreadsheet" : "Browse Files"}
                        </button>

                        <a 
                          href="../../../assets/file/sample_reference.xlsx" 
                          className="btn btn-outline-success px-3 py-2 rounded-pill fw-semibold"
                          style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FontAwesomeIcon icon={faDownload} className="me-2" />
                          Download Sample Template
                        </a>
                      </div>

                      {/* Active File Banner */}
                      {csvFile && (
                        <div className="mt-3 p-2 px-3 rounded-4 border text-start d-flex align-items-center justify-content-between mx-auto" style={{ maxWidth: '480px', background: '#f8fafc' }}>
                          <div className="d-flex align-items-center gap-3 overflow-hidden">
                            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <FontAwesomeIcon icon={faFileAlt} style={{ color: '#16a34a', fontSize: '16px' }} />
                            </div>
                            <div className="text-truncate">
                              <div className="fw-bold text-dark text-truncate" style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>{csvFile.name}</div>
                              <div className="text-muted" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>{(csvFile.size / 1024).toFixed(1)} KB • Ready for Broadcast</div>
                            </div>
                          </div>
                          <span className="badge bg-success rounded-pill px-3 py-1 d-flex align-items-center gap-1" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                            <FontAwesomeIcon icon={faCheck} /> Loaded
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Audience Settings & Deduplication Card */}
                  <div 
                    className="card border-0 shadow-sm mb-3" 
                    style={{
                      borderRadius: '14px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div className="card-body p-3 px-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                      <div className="d-flex align-items-center gap-3">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: allowDuplicates ? '#dcfce7' : '#e0f2fe',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <FontAwesomeIcon 
                            icon={faCopy} 
                            style={{ color: allowDuplicates ? '#16a34a' : '#0284c7', fontSize: '16px' }} 
                          />
                        </div>
                        <div>
                          <h6 className="fw-bold text-dark mb-0" style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                            Allow Duplicate Phone Numbers
                          </h6>
                          <p className="text-muted mb-0" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                            {allowDuplicates
                              ? "Messages will be delivered to every repeated phone number found in the file."
                              : "Repeated phone numbers will be automatically deduplicated (only 1 message sent per number)."}
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-3 flex-shrink-0">
                        <span 
                          className="badge rounded-pill px-3 py-1 fw-semibold"
                          style={{
                            background: allowDuplicates ? '#dcfce7' : '#f1f5f9',
                            color: allowDuplicates ? '#15803d' : '#475569',
                            border: `1px solid ${allowDuplicates ? '#bbf7d0' : '#e2e8f0'}`,
                            fontSize: 'var(--font-size-xs, 0.75rem)'
                          }}
                        >
                          {allowDuplicates ? "✓ Duplicates Allowed" : "🛡️ Deduplication Active"}
                        </span>

                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="allowDuplicatesSwitch"
                            checked={allowDuplicates}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setAllowDuplicates(checked);
                              if (csvFile) {
                                processFile(csvFile, checked);
                              }
                            }}
                            style={{ cursor: 'pointer', width: '2.6em', height: '1.3em' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* File Analysis KPI Cards */}
                  {fileAnalysis && (
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold text-dark mb-0" style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                          File Analysis Metrics
                        </h6>
                        <span className="text-muted" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                          {isProcessingFile ? "Updating metrics..." : "Real-time verification"}
                        </span>
                      </div>

                      <div className="row g-2">
                        {/* Total Contacts */}
                        <div className="col-6 col-md-3">
                          <div className="card border-0 shadow-sm h-100 rounded-3" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
                            <div className="card-body p-2 text-center">
                              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px' }}>
                                <FontAwesomeIcon icon={faUsers} style={{ fontSize: 'var(--font-size-xs)' }} />
                              </div>
                              <div className="text-muted fw-semibold" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Total Records</div>
                              <div className="fw-bold text-dark mt-1" style={{ fontSize: 'var(--font-size-lg, 1.125rem)' }}>{fileAnalysis.totalContacts}</div>
                              <div className="text-muted" style={{ fontSize: '10.5px' }}>Imported rows</div>
                            </div>
                          </div>
                        </div>

                        {/* Valid Contacts */}
                        <div className="col-6 col-md-3">
                          <div className="card border-0 shadow-sm h-100 rounded-3" style={{ background: '#ffffff', border: '1px solid #dcfce7' }}>
                            <div className="card-body p-2 text-center">
                              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px' }}>
                                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 'var(--font-size-xs)' }} />
                              </div>
                              <div className="text-muted fw-semibold" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Valid Contacts</div>
                              <div className="fw-bold text-success mt-1" style={{ fontSize: 'var(--font-size-lg, 1.125rem)' }}>{fileAnalysis.validContacts}</div>
                              <div className="text-success" style={{ fontSize: '10.5px', fontWeight: 600 }}>Ready to send</div>
                            </div>
                          </div>
                        </div>

                        {/* Invalid Format */}
                        <div className="col-6 col-md-3">
                          <div className="card border-0 shadow-sm h-100 rounded-3" style={{ background: '#ffffff', border: '1px solid #fef3c7' }}>
                            <div className="card-body p-2 text-center">
                              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px' }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 'var(--font-size-xs)' }} />
                              </div>
                              <div className="text-muted fw-semibold" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Invalid Format</div>
                              <div className="fw-bold text-warning mt-1" style={{ fontSize: 'var(--font-size-lg, 1.125rem)' }}>{fileAnalysis.invalidFormat}</div>
                              <div className="text-muted" style={{ fontSize: '10.5px' }}>Missing name/number</div>
                            </div>
                          </div>
                        </div>

                        {/* Duplicates */}
                        <div className="col-6 col-md-3">
                          <div className="card border-0 shadow-sm h-100 rounded-3" style={{ background: '#ffffff', border: '1px solid #fce7f3' }}>
                            <div className="card-body p-2 text-center">
                              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fce7f3', color: '#db2777', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px' }}>
                                <FontAwesomeIcon icon={faCopy} style={{ fontSize: 'var(--font-size-xs)' }} />
                              </div>
                              <div className="text-muted fw-semibold" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Duplicate Numbers</div>
                              <div className="fw-bold text-danger mt-1" style={{ fontSize: 'var(--font-size-lg, 1.125rem)' }}>{fileAnalysis.duplicates}</div>
                              <div style={{ fontSize: '10.5px', fontWeight: 600 }} className={fileAnalysis.allowDuplicates ? "text-success" : "text-muted"}>
                                {fileAnalysis.allowDuplicates ? "✓ Included in Send" : "Excluded from Send"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom Navigation (Fixed Footer) */}
                <div className="campaign-step-footer d-flex justify-content-between align-items-center">
                  <button 
                    type="button"
                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handlePrevStep}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous Step
                  </button>

                  <button 
                    type="button"
                    className="btn btn-campaign-primary px-4 px-md-5 py-2 rounded-pill fw-semibold shadow" 
                    style={{
                      fontSize: 'var(--font-size-sm, 0.875rem)',
                      cursor: csvFile ? 'pointer' : 'not-allowed',
                    }}
                    onClick={handleNextStep} 
                    disabled={!csvFile}
                  >
                    Continue to Audience Selection
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3 (CSV) / Step 2 (Broadcast): Select & Review Audience */}
            {((type === "csv" && currentStep === 3) || (type === "broadcast" && currentStep === 2)) && (
              <div className="campaign-step-card w-100">
                {/* Scrollable Body */}
                <div className="campaign-step-scroll-body d-flex flex-column">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 pb-3 mb-3 border-bottom">
                    <div>
                      <span 
                        className="badge px-3 py-1 rounded-pill mb-2" 
                        style={{ 
                          background: 'rgba(7,94,84,0.1)', 
                          color: 'var(--companycolor, #075E54)', 
                          fontWeight: 700, 
                          fontSize: 'var(--font-size-xs, 0.75rem)', 
                          letterSpacing: '0.5px' 
                        }}
                      >
                        STEP {currentStep} • AUDIENCE REVIEW
                      </span>
                      <h3 className="fw-bold text-dark mb-1" style={{ fontSize: 'var(--font-size-2xl, 1.5rem)', letterSpacing: '-0.5px' }}>
                        Select &amp; Review Audience
                      </h3>
                      <p className="text-muted mb-0" style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                        {type === "csv"
                          ? "Review the recipients and mapped parameters from your uploaded spreadsheet."
                          : "Preview your contacts and select the recipients you want to include in this campaign."
                        }
                      </p>
                    </div>

                    {/* Action or Metric Chip */}
                    <div className="d-flex align-items-center gap-2">
                      {type === "broadcast" ? (
                        <button 
                          className="btn btn-outline-success btn-sm rounded-pill px-3 py-2 fw-semibold" 
                          style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                          onClick={fetchContacts} 
                          disabled={loadingContacts}
                        >
                          <FontAwesomeIcon icon={faSyncAlt} className={`me-2 ${loadingContacts ? 'fa-spin' : ''}`} />
                          Refresh Contacts
                        </button>
                      ) : (
                        <div 
                          className="px-3 py-2 rounded-4 d-flex align-items-center gap-2 border"
                          style={{ background: 'linear-gradient(135deg, rgba(7,94,84,0.04) 0%, rgba(18,140,126,0.08) 100%)' }}
                        >
                          <div 
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              background: 'var(--companycolor, #075E54)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#ffffff',
                              fontSize: 'var(--font-size-xs, 0.75rem)'
                            }}
                          >
                            <FontAwesomeIcon icon={faUsers} />
                          </div>
                          <div>
                            <div className="fw-bold text-dark" style={{ fontSize: 'var(--font-size-sm, 0.875rem)', lineHeight: 1.2 }}>
                              {csvPreview ? csvPreview.length : 0} Recipients
                            </div>
                            <div className="text-muted" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                              Ready to Broadcast
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Audience Table Container - Flex 1 Internal Scroll */}
                  <div 
                    className="audience-table-wrapper mb-3" 
                    style={{ flex: '1 1 auto', minHeight: '260px', overflowY: 'auto' }}
                  >
                    <table className="table audience-table table-hover align-middle mb-0 text-center">
                      <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        {type === "csv" ? (
                          <tr>
                            <th style={{ width: "60px", fontSize: 'var(--font-size-xs, 0.75rem)' }}>#</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)', textAlign: 'left' }}>Recipient Name</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Phone Number</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Param 1</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Param 2</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Param 3</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Param 4</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Param 5</th>
                          </tr>
                        ) : (
                          <tr>
                            <th style={{ width: "50px" }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectAll}
                                onChange={handleSelectAll}
                              />
                            </th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)', textAlign: 'left' }}>Name</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Mobile Number</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Email Address</th>
                            <th style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>Location</th>
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {type === "broadcast" ? (
                          customers.map((c) => (
                            <Contactlist key={c.id} customer={c} handleSelectCustomer={handleSelectCustomer} />
                          ))
                        ) : (
                          csvPreview && csvPreview.length > 0 ? (
                            csvPreview.map((contact, index) => (
                              <tr key={contact.id || index}>
                                <td>
                                  <span 
                                    className="badge rounded-pill"
                                    style={{
                                      background: '#f1f5f9',
                                      color: '#64748b',
                                      fontSize: 'var(--font-size-xs, 0.75rem)',
                                      fontWeight: 600,
                                      padding: '4px 8px'
                                    }}
                                  >
                                    {contact.id || index + 1}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'left' }}>
                                  <span className="fw-semibold text-dark" style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                                    {contact.name || '—'}
                                  </span>
                                </td>
                                <td>
                                  <span 
                                    className="badge rounded-pill fw-semibold" 
                                    style={{ 
                                      background: 'rgba(7,94,84,0.08)', 
                                      color: 'var(--companycolor, #075E54)', 
                                      fontSize: 'var(--font-size-sm, 0.875rem)',
                                      letterSpacing: '0.3px',
                                      padding: '5px 12px'
                                    }}
                                  >
                                    {contact.mobile}
                                  </span>
                                </td>
                                <td>
                                  {contact.parameter1 ? (
                                    <span className="badge rounded-pill bg-light text-dark border px-2 py-1" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                                      {contact.parameter1}
                                    </span>
                                  ) : (
                                    <span className="text-muted" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>—</span>
                                  )}
                                </td>
                                <td>
                                  {contact.parameter2 ? (
                                    <span className="badge rounded-pill bg-light text-dark border px-2 py-1" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                                      {contact.parameter2}
                                    </span>
                                  ) : (
                                    <span className="text-muted" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>—</span>
                                  )}
                                </td>
                                <td>
                                  {contact.parameter3 ? (
                                    <span className="badge rounded-pill bg-light text-dark border px-2 py-1" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                                      {contact.parameter3}
                                    </span>
                                  ) : (
                                    <span className="text-muted" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>—</span>
                                  )}
                                </td>
                                <td>
                                  {contact.parameter4 ? (
                                    <span className="badge rounded-pill bg-light text-dark border px-2 py-1" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                                      {contact.parameter4}
                                    </span>
                                  ) : (
                                    <span className="text-muted" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>—</span>
                                  )}
                                </td>
                                <td>
                                  {contact.parameter5 ? (
                                    <span className="badge rounded-pill bg-light text-dark border px-2 py-1" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                                      {contact.parameter5}
                                    </span>
                                  ) : (
                                    <span className="text-muted" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>—</span>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-5">
                                <div className="text-muted" style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                                  No contacts imported yet. Please upload a valid Excel spreadsheet (.xlsx, .xls).
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer Info Box */}
                  {type === "csv" && (
                    <div 
                      className="p-3 rounded-3 mb-2 d-flex align-items-center justify-content-between flex-wrap gap-2"
                      style={{ background: 'rgba(7,94,84,0.04)', border: '1px solid rgba(7,94,84,0.12)' }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--wa-light-green, #25D366)', fontSize: 'var(--font-size-base, 1rem)' }} />
                        <span className="text-dark" style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                          <strong>All {csvPreview ? csvPreview.length : 0} contacts</strong> from your spreadsheet will be included in this broadcast campaign.
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation Buttons (Fixed Footer) */}
                <div className="campaign-step-footer d-flex justify-content-between align-items-center">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handlePrevStep}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous
                  </button>

                  <button
                    type="button"
                    className="btn btn-campaign-primary px-4 px-md-5 py-2 rounded-pill fw-semibold shadow"
                    style={{
                      fontSize: 'var(--font-size-sm, 0.875rem)',
                      cursor: (type === "broadcast" ? customers.filter(c => c.selected).length > 0 : !!csvPreview) ? 'pointer' : 'not-allowed',
                    }}
                    onClick={handleNextStep}
                    disabled={type === "broadcast" ? customers.filter(c => c.selected).length === 0 : !csvPreview}
                  >
                    Next Step
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 4 (CSV) / Step 3 (Broadcast): Create Message / Template Selection */}
            {((type === "csv" && currentStep === 4) || (type === "broadcast" && currentStep === 3)) && (
              <div className="campaign-step-card w-100">
                {file != null && (
                  <UploadModal FilePathUpdate={FilePathUpdate} file={file} url={`${domain()}/whatsapp/campaign/uplode-file`} />
                )}

                {/* Internal Scrollable Content Body */}
                <div className="campaign-step-scroll-body">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3 pb-2 border-bottom">
                    <div>
                      <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.75rem)', letterSpacing: '0.5px' }}>
                        STEP {currentStep} • TEMPLATE SELECTION
                      </span>
                      <h4 className="fw-bold mb-1 text-dark">Select &amp; Map Template</h4>
                      <p className="text-muted mb-0 small">Map variables to contact fields or upload media files / enter static values.</p>
                    </div>
                    {selectedTemplate && (
                      <div className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1">
                        <FontAwesomeIcon icon={faCheck} />
                        <span>Selected: <strong>{selectedTemplate.name || selectedTemplate.template_name}</strong></span>
                      </div>
                    )}
                  </div>

                  {loadingTemplates ? (
                    <div className="text-center py-5">
                      <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-success mb-2" />
                      <p className="text-muted">Loading templates...</p>
                    </div>
                  ) : (
                    <div className="row g-3 mb-3">
                      {templates.map((t) => (
                        <Templatelist
                          key={t.id}
                          template={t}
                          selectedTemplate={selectedTemplate}
                          handleTemplateSelectionFunction={handleTemplateSelection}
                        />
                      ))}
                    </div>
                  )}

                  {/* Template Variables Mapping */}
                  {Object.keys(templateVariables).length > 0 && (
                    <div className="mt-4 pt-3 border-top">
                      <h5 className="fw-semibold text-dark">Map Template Variables</h5>
                      <p className="text-muted mb-3 small">
                        {type === "csv"
                          ? "Map template variables to data from your uploaded file or enter static values."
                          : "Map template variables to contact fields from your contact list or enter static values."
                        }
                      </p>

                      <div className="row g-3 m-0 p-0">
                        {Object.keys(templateVariables).map((vKey, index) => {
                          const isHeaderVar = vKey.startsWith('header');
                          const activeContentType = fileType.current || templateVariables[vKey]?.content_type;
                          const isMediaHeader = isHeaderVar && (activeContentType === 'IMAGE' || activeContentType === 'VIDEO' || activeContentType === 'DOCUMENT' || activeContentType === 'AUDIO');
                          const isStatic = templateVariables[vKey]?.value_type === 'STATIC';
                          const isDynamic = templateVariables[vKey]?.value_type === 'DYNAMIC';

                          return (
                            <div key={vKey} className="col-12 p-0 m-0 my-1">
                              <div className="card-body card m-0 p-0 px-2 border rounded-3">
                                <div className="row align-items-center align-content-center justify-content-between m-0 py-2">

                                  {/* Variable Label */}
                                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                                    <label className="form-label fw-semibold mb-0">
                                      {isHeaderVar ? "Header" : vKey.startsWith('body_') ? `Body Variable ${vKey.split('_')[1]}` : `Variable ${index + 1}`}:
                                    </label>
                                  </div>

                                  {/* Select Value Type */}
                                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                                    <label className="form-label small mb-1">Select Value Type</label>
                                    <select
                                      className="form-select form-select-sm"
                                      value={templateVariables[vKey]?.value_type || ""}
                                      onChange={(e) => {
                                        if (e.target.value === "STATIC") {
                                          handleVariableMapping(vKey, "STATIC");
                                        } else if (e.target.value === "DYNAMIC") {
                                          handleVariableMapping(vKey, "DYNAMIC");
                                        } else {
                                          handleVariableMapping(vKey, "");
                                        }
                                      }}
                                    >
                                      <option value="">Select Value Type</option>
                                      <option value="STATIC">Static</option>
                                      <option value="DYNAMIC">Dynamic</option>
                                    </select>
                                  </div>

                                  {/* Header Media File Upload & File URL Box (for STATIC Header Media: IMAGE, VIDEO, AUDIO, DOCUMENT) */}
                                  {isStatic && isMediaHeader && (
                                    <>
                                      <div className="col-12 col-md-3 mb-2 mb-md-0">
                                        <label className="form-label small mb-1">{`Upload ${activeContentType}`}</label>
                                        <input
                                          type="file"
                                          className="form-control form-control-sm pt-1 pl-1"
                                          accept={
                                            activeContentType === "IMAGE" ? "image/*" :
                                              activeContentType === "VIDEO" ? "video/*" :
                                                activeContentType === "AUDIO" ? "audio/*" :
                                                  activeContentType === "DOCUMENT" ? "*/*" : ""
                                          }
                                          onChange={(e) => {
                                            setIsUploadingImage(true);
                                            setFile(e.target.files[0]);
                                          }}
                                          disabled={isUploadingImage}
                                        />
                                        {isUploadingImage && (
                                          <small className="text-muted">Uploading...</small>
                                        )}
                                      </div>
                                      <div className="col-12 col-md-3">
                                        <label className="form-label small mb-1">File URL:</label>
                                        <input
                                          type="url"
                                          required
                                          readOnly
                                          pattern="https?://.*"
                                          className="form-control form-control-sm"
                                          placeholder="File URL will appear here"
                                          value={templateVariables[vKey]?.value || fileUrl.current || ""}
                                        />
                                      </div>
                                    </>
                                  )}

                                  {/* Text Input for STATIC Body OR STATIC Header when format is TEXT */}
                                  {isStatic && (!isHeaderVar || fileType.current === null) && (
                                    <div className="col-12 col-md-4">
                                      <label className="form-label small mb-1">Enter Static Value</label>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Enter static value"
                                        value={templateVariables[vKey]?.value || ""}
                                        onChange={(e) => handleVariableMapping(vKey, 'STATIC', e.target.value, fileType.current === null ? 'TEXT' : fileType.current)}
                                      />
                                    </div>
                                  )}

                                  {/* Dropdown for DYNAMIC Mapping */}
                                  {isDynamic && (
                                    <div className="col-12 col-md-4">
                                      <label className="form-label small mb-1">Map to Contact Field:</label>
                                      <select
                                        className="form-select form-select-sm"
                                        value={templateVariables[vKey]?.value || ""}
                                        onChange={(e) => handleVariableMapping(vKey, 'DYNAMIC', e.target.value, fileType.current === null ? 'TEXT' : fileType.current)}
                                      >
                                        <option value="">Select contact field</option>
                                        {type === "broadcast" && (
                                          <>
                                            <option value="name">Name</option>
                                            <option value="mobile">Mobile Number</option>
                                            <option value="email">Email Address</option>
                                            <option value="address">Address</option>
                                            <option value="city">City</option>
                                            <option value="district">District</option>
                                            <option value="state">State</option>
                                            <option value="pin">PIN Code</option>
                                          </>
                                        )}
                                        {type === "csv" && csvPreview && csvPreview.length > 0 && (
                                          <>
                                            <option value="name">Name</option>
                                            <option value="mobile">Mobile</option>
                                            <option value="parameter1">Parameter 1</option>
                                            <option value="parameter2">Parameter 2</option>
                                            <option value="parameter3">Parameter 3</option>
                                            <option value="parameter4">Parameter 4</option>
                                            <option value="parameter5">Parameter 5</option>
                                          </>
                                        )}
                                      </select>
                                    </div>
                                  )}

                                  {!isStatic && !isDynamic && (
                                    <div className="col-12 col-md-4"></div>
                                  )}

                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Fixed Visible Action Footer */}
                <div className="campaign-step-footer d-flex justify-content-between align-items-center">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handlePrevStep}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-campaign-primary px-4 px-md-5 py-2 rounded-pill fw-semibold shadow" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handleNextStep} 
                    disabled={!selectedTemplate}
                  >
                    Next Step
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 5 (CSV) / Step 4 (Broadcast): Test & Preview */}
            {((type === "csv" && currentStep === 5) || (type === "broadcast" && currentStep === 4)) && (
              <div className="campaign-step-card w-100">
                <div className="campaign-step-scroll-body">
                  <div className="row g-3 g-lg-4">
                    <div className="col-12 col-lg-6">
                      <div className="card border-0 shadow-sm rounded-4 p-4 h-100 d-flex flex-column justify-content-between" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div>
                          <span className="badge px-3 py-1 rounded-pill mb-2 align-self-start" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                            STEP {currentStep} • QUALITY CHECK
                          </span>
                          <h4 className="fw-bold mb-2">Send Test Message</h4>
                          <p className="text-muted mb-4 small">Send a sample message to your phone before launching to verify variables and formatting.</p>

                          <div className="mb-4">
                            <label className="form-label fw-semibold">Phone Number (with country code) *</label>
                            <input
                              type="text"
                              className="form-control form-control-lg rounded-3"
                              placeholder="e.g. 919876543210"
                              value={testPhoneNumber}
                              onChange={(e) => setTestPhoneNumber(e.target.value)}
                            />
                            <div className="form-text mt-2 text-muted small">
                              Include country code without + (e.g. 91 for India).
                            </div>
                          </div>
                        </div>

                        <div>
                          <button
                            type="button"
                            className="btn btn-campaign-primary w-100 py-2.5 rounded-pill fw-semibold shadow-sm"
                            onClick={() => sendCampaign('Test')}
                            disabled={isSendingTest}
                          >
                            <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                            {isSendingTest ? 'Sending Test...' : 'Send Test WhatsApp Message'}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-lg-6 d-flex justify-content-center">
                      <TemplatePreview
                        templateVariables={templateVariables}
                        selectedTemplate={selectedTemplate}
                        sampleData={samplePreviewData}
                      />
                    </div>
                  </div>
                </div>

                <div className="campaign-step-footer d-flex justify-content-between align-items-center">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handlePrevStep}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-campaign-primary px-4 px-md-5 py-2 rounded-pill fw-semibold shadow" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handleNextStep}
                  >
                    Continue to Summary
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 6 (CSV) / Step 5 (Broadcast): Review & Launch */}
            {((type === "csv" && currentStep === 6) || (type === "broadcast" && currentStep === 5)) && (
              <div className="campaign-step-card w-100">
                <div className="campaign-step-scroll-body">
                  <div className="row g-3 g-lg-4">
                    <div className="col-12 col-lg-6">
                      <div className="card border-0 shadow-sm rounded-4 p-4 h-100 d-flex flex-column justify-content-between" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div>
                          {/* Header Badge & Title */}
                          <div className="mb-3">
                            <span className="badge px-3 py-1 rounded-pill mb-2 align-self-start" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.75rem)', letterSpacing: '0.5px' }}>
                              FINAL STEP • CONFIRM &amp; SEND
                            </span>
                            <h4 className="fw-bold mb-1 text-dark">Campaign Summary</h4>
                            <p className="text-muted small mb-0">Review your broadcast configurations before launching.</p>
                          </div>

                          {/* Summary KPI Cards Grid (2x2) */}
                          <div className="row g-2 mb-3">
                            {/* Card 1: Campaign Name */}
                            <div className="col-12 col-sm-6">
                              <div className="summary-kpi-card">
                                <div className="summary-kpi-icon" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)' }}>
                                  <FontAwesomeIcon icon={faBullhorn} />
                                </div>
                                <div className="summary-kpi-content">
                                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Campaign Name</div>
                                  <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.875rem' }} title={campaignName}>
                                    {campaignName || 'Untitled'}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card 2: Scheduled Time */}
                            <div className="col-12 col-sm-6">
                              <div className="summary-kpi-card">
                                <div className="summary-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                                  <FontAwesomeIcon icon={faClock} />
                                </div>
                                <div className="summary-kpi-content">
                                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Scheduled Time</div>
                                  <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.875rem' }}>
                                    {scheduleDate} • {scheduleTime}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card 3: Target Audience */}
                            <div className="col-12 col-sm-6">
                              <div className="summary-kpi-card">
                                <div className="summary-kpi-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                                  <FontAwesomeIcon icon={faUsers} />
                                </div>
                                <div className="summary-kpi-content">
                                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Target Audience</div>
                                  <div className="fw-bold text-success text-truncate" style={{ fontSize: '0.875rem' }}>
                                    {type === "csv" ? csvPreview?.length || 0 : customers.filter(c => c.selected).length} contacts
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card 4: Selected Template */}
                            <div className="col-12 col-sm-6">
                              <div className="summary-kpi-card">
                                <div className="summary-kpi-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                                  <FontAwesomeIcon icon={faLayerGroup} />
                                </div>
                                <div className="summary-kpi-content">
                                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Selected Template</div>
                                  <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.875rem' }} title={selectedTemplate?.name || selectedTemplate?.template_name}>
                                    {selectedTemplate?.name || selectedTemplate?.template_name || '—'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* WhatsApp Policy Confirmation Interactive Card */}
                          <div 
                            className={`compliance-agreement-card mb-3 ${isPolicyConfirmed ? 'is-confirmed' : ''}`}
                            onClick={() => setIsPolicyConfirmed(!isPolicyConfirmed)}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div className="d-flex align-items-center justify-content-center flex-shrink-0">
                                <input
                                  type="checkbox"
                                  className="compliance-checkbox"
                                  id="confirmCheck"
                                  checked={isPolicyConfirmed}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setIsPolicyConfirmed(e.target.checked);
                                  }}
                                />
                              </div>
                              <div className="flex-grow-1">
                                <div className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '0.845rem' }}>
                                  <FontAwesomeIcon icon={faShieldAlt} style={{ color: 'var(--companycolor, #075E54)' }} />
                                  <span>WhatsApp Business Policy Compliance</span>
                                </div>
                                <div className="text-muted small mt-1" style={{ fontSize: '0.75rem', lineHeight: 1.35 }}>
                                  I confirm that this broadcast complies with Meta WhatsApp Business messaging policies and recipient opt-in requirements.
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Schedule & Launch Button */}
                        <div className="mt-2">
                          <button
                            type="button"
                            className="btn btn-campaign-primary w-100 py-3 rounded-pill fw-bold text-uppercase shadow d-flex align-items-center justify-content-center gap-2"
                            style={{
                              fontSize: '0.925rem',
                              letterSpacing: '0.5px'
                            }}
                            onClick={() => sendCampaign('Production')}
                            disabled={isSendingCampaign || !isPolicyConfirmed}
                          >
                            {isSendingCampaign ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Scheduling Campaign...</span>
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faPaperPlane} />
                                <span>Schedule &amp; Launch Campaign</span>
                              </>
                            )}
                          </button>
                        </div>

                      </div>
                    </div>

                    <div className="col-12 col-lg-6 d-flex justify-content-center">
                      <TemplatePreview
                        templateVariables={templateVariables}
                        selectedTemplate={selectedTemplate}
                        sampleData={samplePreviewData}
                      />
                    </div>
                  </div>
                </div>

                <div className="campaign-step-footer d-flex justify-content-between align-items-center">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handlePrevStep}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default BroadcastType;